"use client";

import { useEffect, useRef, useState } from "react";

// ─── Easing ─────────────────────────────────────────────────────
const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";

// ─── Keyframes ──────────────────────────────────────────────────

function PlayAllKeyframes() {
  return (
    <style>{`
      @keyframes briefs-ripple {
        0% { transform: scale(0.9); opacity: 0.6; }
        50% { transform: scale(1.15); opacity: 0; }
        100% { transform: scale(0.9); opacity: 0; }
      }
      @keyframes briefs-ripple-delay {
        0%, 30% { transform: scale(0.9); opacity: 0; }
        31% { opacity: 0.5; }
        65% { transform: scale(1.2); opacity: 0; }
        100% { transform: scale(0.9); opacity: 0; }
      }
      @keyframes briefs-tick {
        0%, 90%, 100% { transform: scale(1); }
        95% { transform: scale(1.12); }
      }
      @keyframes play-confetti-particle {
        0% { transform: translate(0,0) scale(1); opacity: 1; }
        100% { opacity: 0; }
      }
      @keyframes play-bounce {
        0% { transform: scale(1); }
        40% { transform: scale(1.12); }
        70% { transform: scale(0.97); }
        100% { transform: scale(1); }
      }
      @keyframes play-check-pop {
        0% { transform: scale(0.5); opacity: 0; }
        60% { transform: scale(1.15); opacity: 1; }
        100% { transform: scale(1); opacity: 1; }
      }
      @keyframes play-text-up {
        0% { transform: translateY(6px); opacity: 0; }
        100% { transform: translateY(0); opacity: 1; }
      }
    `}</style>
  );
}

// ─── Confetti particles ─────────────────────────────────────────

const CONFETTI_COLORS = [
  "#22c55e",
  "#4ade80",
  "#16a34a",
  "#86efac",
  "#15803d",
  "#22c55e",
];

function ConfettiParticles() {
  return (
    <>
      {CONFETTI_COLORS.map((color, i) => {
        const angle = (i / CONFETTI_COLORS.length) * 360;
        const rad = (angle * Math.PI) / 180;
        const tx = Math.cos(rad) * 28;
        const ty = Math.sin(rad) * 28;
        return (
          <div
            key={i}
            style={{
              position: "absolute",
              width: 5,
              height: 5,
              borderRadius: "50%",
              backgroundColor: color,
              animation: `play-confetti-particle 600ms ${EASE_OUT} ${i * 30}ms forwards`,
              transform: `translate(${tx}px, ${ty}px)`,
            }}
          />
        );
      })}
    </>
  );
}

// ─── Export ──────────────────────────────────────────────────────

interface PlayAllButtonProps {
  totalBriefs: number;
  onPlay: () => void;
  allViewed?: boolean;
  className?: string;
}

export function PlayAllButton({
  totalBriefs,
  onPlay,
  allViewed = false,
  className,
}: PlayAllButtonProps) {
  const [animating, setAnimating] = useState(false);
  const prevAllViewed = useRef(allViewed);
  let circleAnimation = "briefs-tick 2.5s ease-in-out infinite";
  if (allViewed) {
    circleAnimation = "none";
  }
  if (animating) {
    circleAnimation = `play-bounce 500ms ${EASE_OUT}`;
  }

  // Trigger celebration animation when allViewed transitions false → true
  useEffect(() => {
    if (allViewed && !prevAllViewed.current) {
      setAnimating(true);
      const timer = setTimeout(() => setAnimating(false), 800);
      prevAllViewed.current = allViewed;
      return () => clearTimeout(timer);
    }
    prevAllViewed.current = allViewed;
  }, [allViewed]);

  return (
    <>
      <PlayAllKeyframes />
      <button
        className={`group relative flex shrink-0 cursor-pointer items-center gap-3 border-none bg-transparent p-0 text-left ${className ?? ""}`}
        onClick={onPlay}
        title="Play all briefs"
        type="button"
      >
        {/* Ring with ripple animation */}
        <div className="relative flex h-12 w-12 items-center justify-center">
          {/* Confetti burst on completion */}
          {animating && <ConfettiParticles />}

          {/* Ripples — only when not all viewed */}
          {!allViewed && (
            <>
              <div
                className="absolute inset-0 rounded-full border-2 border-[var(--explore-accent-yellow)]"
                style={{ animation: "briefs-ripple 2.5s ease-out infinite" }}
              />
              <div
                className="absolute inset-0 rounded-full border-2 border-[#fbbf24]"
                style={{
                  animation: "briefs-ripple-delay 2.5s ease-out infinite",
                }}
              />
              <div className="absolute inset-[3px] rounded-full border-2 border-[var(--explore-accent-yellow)]/40" />
            </>
          )}

          {/* Circle — stays yellow always */}
          <div
            className="flex h-8 w-8 items-center justify-center rounded-full"
            style={{
              backgroundColor: "var(--explore-accent-yellow)",
              animation: circleAnimation,
            }}
          >
            {allViewed ? (
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="var(--explore-on-accent-text)"
                strokeWidth={2.5}
                style={{
                  animation: animating
                    ? `play-check-pop 400ms ${EASE_OUT} 150ms both`
                    : "none",
                }}
                viewBox="0 0 24 24"
              >
                <path
                  d="M5 13l4 4L19 7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                className="ml-0.5 h-3.5 w-3.5"
                fill="var(--explore-on-accent-text)"
                viewBox="0 0 24 24"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </div>
        </div>

        {/* Label */}
        <span className="flex flex-col leading-none">
          <span
            className="font-semibold text-[1.1rem] text-[var(--explore-text-primary)]"
            style={{ fontFamily: "var(--explore-card-title-stack)" }}
          >
            Briefs
          </span>
          <span
            className="mt-0.5 text-[0.68rem] text-[var(--explore-text-muted)]"
            style={{
              fontFamily: "var(--homepage-sans-stack)",
              animation: animating
                ? `play-text-up 300ms ${EASE_OUT} 200ms both`
                : "none",
            }}
          >
            {allViewed ? "All read!" : `${totalBriefs} briefs`}
          </span>
        </span>
      </button>
    </>
  );
}
